import { ohneAbleitung } from '../domain/abgeleitet';
import { felderAusTeilen } from '../domain/dateiname';
import { istGeraet } from '../platform/gleis';
import { MultipartUpload, type MultipartUploadPlugin } from '../platform/multipart';

/**
 * Der `multipart/form-data`-Transport — **beide Gleise**.
 *
 * Hier steht, was wirklich beiden dient: die Wahl zwischen `fetch` (Browser, Tests) und dem
 * nativen Plugin (Gerät), die Base64-Umrechnung, der Medientyp und das Lesen der Felder aus dem
 * Dateinamen unserer Ablage.
 *
 * ⚠️ **Die Felder selbst gehören nicht hierher.** Classics dreizehn mit `progressTick` und
 * `version=2.0` stehen in `classic/hochladen.ts`, Relay' neun in `anhaenge.ts`. Wer sie
 * zusammenzöge, verkrüppelte Relay an einem Protokoll, das abgelöst werden soll
 * (`memory/zweigleisig.md`).
 *
 * ⚠️ **Die Kopfzeilen kommen von aussen.** Classic meldet sich mit HTTP Basic an, Relay mit
 * einem Bearer-Token; ein Transport, der eines von beidem selbst bildete, taugte nur für ein
 * Gleis.
 */

/**
 * Medientyp aus der Endung.
 *
 * Dieselbe Liste, die der Classic-Client bildet, um zwei Endungen erweitert, die es erst bei uns
 * gibt: `m4a` (unsere Tonaufnahme) und `svg` (Handschrift und Markierebene).
 */
const MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  mp4: 'video/mp4',
  m4a: 'audio/mp4',
  caf: 'audio/x-caf',
  mp3: 'audio/mp3',
  svg: 'image/svg+xml',
};

export function mimeVonName(name: string): string {
  const punkt = name.lastIndexOf('.');
  if (punkt < 0) return 'application/octet-stream';
  return MIME[name.slice(punkt + 1).toLowerCase()] ?? 'application/octet-stream';
}

/**
 * Die `schlüssel=wert`-Paare aus einem Dateinamen unserer Ablage.
 *
 * Bewusst nachsichtig: fehlt ein Teil, fehlt er eben. Ein Wurf hielte den ganzen Stapel auf,
 * und eine Datei ohne Zuordnung ist immer noch besser auf dem Server als auf dem iPad.
 *
 * ⚠️ **Die Ableitung fällt zuerst.** Eine Markierebene heisst wie ihr Foto samt Endung, mit
 * `-overlay.svg` dahinter; ein blosses Abschneiden am letzten Punkt liesse `itemID=42.jpeg-overlay`
 * stehen (`domain/abgeleitet.ts`). Die Wache steht **hier**, bei allen Lesern zugleich: sie
 * stand bis zum 2026-09-14 nur an einer der beiden Aufrufstellen, und die andere schickte die
 * Ebene eines Stationskommentars mit `stationID=-1` hinaus — dort ist `stationID` das letzte
 * Feld des Namens.
 */
export function felderAusName(dateiname: string): Record<string, string> {
  const roh = ohneAbleitung(dateiname);
  const punkt = roh.lastIndexOf('.');
  const ohneEndung = punkt < 0 ? roh : roh.slice(0, punkt);
  /*
   * ⚠️ **Das Zerlegen selbst steht in `domain/dateiname.ts`**, seit der Name am 2026-09-21
   * kurze Schlüssel und kompakte Kennungen bekam. Es gibt die ausgeschriebenen Schlüssel und
   * die vollen UUIDs zurück — die Aufrufer hier lesen weiter `examID`, `candidateID` und den
   * Rest, als hätte sich nichts geändert, und ältere Dateien fahren unverändert mit.
   */
  return felderAusTeilen(ohneEndung);
}

export interface MultipartAufruf {
  url: string;
  felder: Record<string, string>;
  /** `feld`: der Name des Dateiteils, Vorgabe `file` — Relays Log-ZIP will `datei`. */
  datei: { name: string; mime: string; base64: string; feld?: string };
}

/**
 * Der Transport für einen multipart-Aufruf.
 *
 * ⚠️ **Die einzige Stelle, die je Plattform verschieden ist.** `CapacitorHttp` kann Binärdaten
 * in einem `multipart/form-data` nicht zuverlässig tragen (Capacitors eigene Beschreibung
 * verweist für Dateien auf ein eigenes Plugin). Im Browser und in den Tests genügt `FormData`
 * samt `fetch`; auf dem iPad trägt das native Plugin. Alles darüber — Felder, Reihenfolge,
 * Fortschritt, Fehlerbehandlung — hängt nicht daran.
 */
export type MultipartSender = (aufruf: MultipartAufruf) => Promise<{
  ok: boolean;
  status: number;
  text: string;
}>;

/**
 * Base64 in Bytes. `atob` gibt eine Zeichenkette, in der jedes Zeichen ein Byte **ist** — sie
 * als Text in einen Blob zu legen hiesse, sie in UTF-8 umzukodieren, und jedes Byte über 127
 * würde zu zweien. Ein PNG käme so kaputt an.
 */
export function bytesAusBase64(base64: string): ArrayBuffer {
  const roh = atob(base64);
  const puffer = new ArrayBuffer(roh.length);
  const bytes = new Uint8Array(puffer);
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
  // ⚠️ Der **Puffer**, nicht die Sicht darauf: seit TypeScript 5.7 ist `Uint8Array` an seinen
  // Puffertyp gebunden und passt nicht mehr ohne Weiteres in `BlobPart`.
  return puffer;
}

/**
 * Der Transport über `FormData` und `fetch` — **Browser und Tests**.
 *
 * ⚠️ **Am Classic-Server trägt dieser Weg auf dem iPad nicht.** Der schickt keine
 * `Access-Control-*`-Kopfzeilen; aus dem WKWebView heraus ist jeder Aufruf fremdherkünftig,
 * und `fetch` scheitert, bevor etwas hinausgeht (derselbe Grund, aus dem der gewöhnliche
 * Verkehr über `CapacitorHttp` läuft, s. `client.ts`).
 */
export function fetchMultipartSender(
  kopfzeilen: Record<string, string>,
  hole: typeof fetch = globalThis.fetch.bind(globalThis),
): MultipartSender {
  return async ({ url, felder, datei }) => {
    const form = new FormData();
    for (const [name, wert] of Object.entries(felder)) form.append(name, wert);
    form.append(
      datei.feld ?? 'file',
      new Blob([bytesAusBase64(datei.base64)], { type: datei.mime }),
      datei.name,
    );

    // ⚠️ **Kein `Content-Type` von Hand.** Die Grenzzeichenkette entsteht erst beim Senden;
    // wer den Kopf selbst setzt, kündigt eine Grenze an, die im Rumpf nicht vorkommt, und der
    // Server findet kein einziges Feld.
    const antwort = await hole(url, { method: 'POST', headers: kopfzeilen, body: form });
    return { ok: antwort.ok, status: antwort.status, text: await antwort.text() };
  };
}

/**
 * Der Transport über das native Plugin — **das Gerät**.
 *
 * Der Rumpf entsteht dort, nicht hier: die Grenzzeichenkette gehört zu dem, der die Bytes
 * zusammensetzt (`ios/App/App/MultipartUploadPlugin.swift`).
 */
export function nativerMultipartSender(
  kopfzeilen: Record<string, string>,
  plugin: MultipartUploadPlugin = MultipartUpload,
): MultipartSender {
  return async ({ url, felder, datei }) =>
    plugin.upload({
      url,
      fields: felder,
      headers: kopfzeilen,
      fileName: datei.name,
      mimeType: datei.mime,
      base64: datei.base64,
      fileField: datei.feld,
    });
}

/**
 * Der Transport, der zur Umgebung passt.
 *
 * ⚠️ **Die Wahl fällt hier und nirgends sonst.** `fetch` scheitert auf dem Gerät an CORS, das
 * Plugin gibt es im Browser nicht — wer die Wahl an zwei Stellen träfe, hätte irgendwann eine
 * Umgebung, in der beide Wege falsch sind.
 */
export function multipartSender(
  kopfzeilen: Record<string, string>,
  optionen: {
    nativ?: boolean;
    plugin?: MultipartUploadPlugin;
    hole?: typeof fetch;
  } = {},
): MultipartSender {
  const nativ = optionen.nativ ?? istGeraet();
  return nativ
    ? nativerMultipartSender(kopfzeilen, optionen.plugin)
    : fetchMultipartSender(kopfzeilen, optionen.hole);
}
